// Auto-fill missing appt dates across a dispatch scope when a driver is being
// assigned (or about to be saved). Extracted from LegsGrid's maybeAutoFillDates
// so the same logic can run both at driver-assignment time (immediate UI
// feedback in the leg cells) and at save time (safety net for any post-
// assignment leg edits that left new holes — adding a leg to the scope,
// splitting via the Drop toggle, changing legtype, etc.).

import type { OrderLeg } from '../services/orders.service';
import { getDispatchScope } from './orderLegDiff';
import { formatMdYDate, parseMdYDate } from './dateUtils';

export type ApptDateFills = Record<
  number,
  { origApptDate?: string; destApptDate?: string }
>;

function todayLocalMidnight(): Date {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return t;
}

/**
 * Returns the appt-date backfills needed for the dispatch scope that
 * `targetLegrecno` belongs to. The map is keyed by legrecno; each entry only
 * carries the side(s) (`origApptDate` / `destApptDate`) that are currently
 * empty and should be filled. Returns `null` when the gate fails, and an
 * empty object when nothing needs filling.
 *
 * Gate: when `targetLegrecno` isn't the first leg in the order, the leg
 * preceding the scope must have BOTH orig and dest appt dates set, and both
 * today-or-past. Future-dated prior dates suppress fill — the assumption is
 * "we're not dispatching this right now."
 *
 * Fill priority for a missing side:
 *   1. The other side of the same leg (orig ↔ dest mirror)
 *   2. Any non-empty date elsewhere in the scope
 *   3. Today (formatted MM/DD/YYYY)
 */
export function computeScopeApptFills(
  targetLegrecno: number,
  allLegs: OrderLeg[],
  today: Date = todayLocalMidnight(),
): ApptDateFills | null {
  const idx = allLegs.findIndex((l) => l.legrecno === targetLegrecno);
  if (idx === -1) return null;

  const scope = getDispatchScope(targetLegrecno, allLegs);
  if (scope.length === 0) return null;
  const scopeStartLegrecno = scope[0];
  const scopeStartIdx = allLegs.findIndex(
    (l) => l.legrecno === scopeStartLegrecno,
  );

  if (idx > 0) {
    const prevLeg =
      scopeStartIdx > 0 ? allLegs[scopeStartIdx - 1] : allLegs[idx - 1];
    const prevOrig = prevLeg.origApptDate;
    const prevDest = prevLeg.destApptDate;
    if (!prevOrig || !prevDest) return null;
    const prevOrigDate = parseMdYDate(prevOrig);
    const prevDestDate = parseMdYDate(prevDest);
    if (prevOrigDate && prevOrigDate > today) return null;
    if (prevDestDate && prevDestDate > today) return null;
  }

  const scopeLegs = scope
    .map((lr) => allLegs.find((l) => l.legrecno === lr))
    .filter((l): l is OrderLeg => !!l);

  const todayFormatted = formatMdYDate(today);
  const pickFillDate = (
    targetLr: number,
    side: 'orig' | 'dest',
  ): string => {
    const targetLeg = scopeLegs.find((l) => l.legrecno === targetLr);
    if (targetLeg) {
      const otherField = side === 'orig' ? 'destApptDate' : 'origApptDate';
      const otherVal = String(targetLeg[otherField] || '').trim();
      if (otherVal) return otherVal;
    }
    for (const l of scopeLegs) {
      if (l.legrecno === targetLr) continue;
      const orig = String(l.origApptDate || '').trim();
      if (orig) return orig;
      const dest = String(l.destApptDate || '').trim();
      if (dest) return dest;
    }
    return todayFormatted;
  };

  const fills: ApptDateFills = {};
  for (const sl of scopeLegs) {
    const fill: { origApptDate?: string; destApptDate?: string } = {};
    if (!String(sl.origApptDate || '').trim()) {
      fill.origApptDate = pickFillDate(sl.legrecno, 'orig');
    }
    if (!String(sl.destApptDate || '').trim()) {
      fill.destApptDate = pickFillDate(sl.legrecno, 'dest');
    }
    if (fill.origApptDate || fill.destApptDate) {
      fills[sl.legrecno] = fill;
    }
  }
  return fills;
}

/**
 * Iterates every dispatch scope in `allLegs` that has at least one assigned
 * driver, computes per-scope autofills, and returns the merged map. Used at
 * save time so any scope with a driver gets its missing dates filled in,
 * regardless of whether the user did the assignment first or fiddled with
 * leg structure afterwards.
 */
export function computeAllScopeApptFills(
  allLegs: OrderLeg[],
  today: Date = todayLocalMidnight(),
): ApptDateFills {
  const merged: ApptDateFills = {};
  const seenScopeStarts = new Set<number>();
  for (const leg of allLegs) {
    if (!String(leg.driver || '').trim()) continue;
    const scope = getDispatchScope(leg.legrecno, allLegs);
    if (scope.length === 0) continue;
    const scopeStart = scope[0];
    if (seenScopeStarts.has(scopeStart)) continue;
    seenScopeStarts.add(scopeStart);
    const scopeFills = computeScopeApptFills(leg.legrecno, allLegs, today);
    if (scopeFills) Object.assign(merged, scopeFills);
  }
  return merged;
}
